import { Controller } from "@hotwired/stimulus"
import { toast } from "../services/toast"

export default class extends Controller {
  static targets = [
    "tableBody",
    "fileInput",
    "progressContainer",
    "progressBar",
    "progressText",
    "errorSummary",
    "errorList",
    "rowCount",
    "validCount",
    "errorCount",
    "totalAmount",
    "supplierSelect",
    "orderNumber",
    "previewButton",
    "importButton",
    "productDatalist"
  ]

  static values = {
    parseUrl: String,
    previewUrl: String,
    importUrl: String,
    statusUrlTemplate: String,
    productSuggestionsUrl: String,
    productFormDataUrl: String,
    validateUniqueCodeUrl: String,
    createProductUrl: String,
    updateProductUrlTemplate: String,
    destroyProductUrlTemplate: String,
    getProductUrlTemplate: String,
    modelCostPreviewUrl: String,
    sizeRuns: Array,
    columns: Array,
    pollInterval: { type: Number, default: 1200 }
  }

  connect() {
    this.importTaskId = null
    this.pollTimer = null
    this.suggestionAbort = null
    this.previewValidCount = 0
    this.previewErrorCount = 0
    this.inFlight = false
    this.autoImportAfterPreview = false

    // Defensive: if Stimulus value parsing fails, ensure we still render a usable grid.
    if (!Array.isArray(this.columnsValue) || this.columnsValue.length === 0) {
      this.columnsValue = [
        { field: "supplier_description_raw", header: "Descripción", required: true },
        { field: "quantity", header: "Cantidad", required: true },
        { field: "unit_price", header: "Precio", required: true },
        { field: "line_total", header: "Importe", required: true },
        { field: "product_code", header: "Producto", required: false },
        { field: "size_run_id", header: "Corrida", required: true }
      ]
    }

    if (!Array.isArray(this.sizeRunsValue)) {
      this.sizeRunsValue = []
    }

    this.setupPasteHandler()
    this.addEmptyRows(5)
    this.updateCounts()
    this.updateTotalAmount()
    this.updateImportButtonState()
  }

  disconnect() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  }

  setupPasteHandler() {
    this.tableBodyTarget.addEventListener("paste", this.handlePaste.bind(this))
  }

  handlePaste(event) {
    const clipboardData = event.clipboardData || window.clipboardData
    const pastedText = clipboardData.getData("text")
    if (!pastedText) return

    const isGridPaste = pastedText.includes("\t") || /\r?\n/.test(pastedText)
    if (!isGridPaste) {
      // Allow normal paste into a single cell/input.
      return
    }

    event.preventDefault()

    let rows = pastedText.split(/\r?\n/).filter(r => r.trim())
    if (rows.length === 0) return

    // If the first row looks like headers, drop it.
    const firstCells = rows[0].split("\t").map(c => c.toLowerCase().trim())
    const headerTokens = ["descripción", "descripcion", "producto", "corrida", "cantidad", "precio", "importe"]
    const headerHits = firstCells.filter(c => headerTokens.includes(c)).length
    if (headerHits >= 3) {
      rows = rows.slice(1)
    }

    if (rows.length === 0) return

    this.tableBodyTarget.innerHTML = ""

    rows.forEach(row => {
      const cells = row.split("\t")
      const values = this.mapPastedCellsToRowValues(cells)
      this.addRow(values)
    })

    this.addEmptyRows(3)
    this.updateCounts()
    this.updateTotalAmount()

    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  mapPastedCellsToRowValues(cells) {
    const raw = Array.isArray(cells) ? cells.map(c => String(c ?? "")) : []
    const compact = raw.map(s => s.trim())
    const nonEmpty = compact.filter(v => v !== "")

    // Excel sometimes includes empty columns (e.g., hidden columns between selected ones).
    // If so, prefer the non-empty cells for layout detection/mapping.
    const effective = (compact.length > nonEmpty.length && nonEmpty.length >= 3) ? nonEmpty : compact

    // Default: assume already aligned to our 6 columns
    const cols = this.columnsValue.map(c => c.field)
    const values = new Array(cols.length).fill("")

    // Common paste: Descripción | Cantidad | Precio | Importe
    if (effective.length === 4) {
      values[cols.indexOf("supplier_description_raw")] = effective[0]
      values[cols.indexOf("quantity")] = effective[1]
      values[cols.indexOf("unit_price")] = effective[2]
      values[cols.indexOf("line_total")] = effective[3]
      return values
    }

    // 5-column variants:
    // - Código | Descripción | Cantidad | Precio | Importe
    // - Descripción | Corrida | Cantidad | Precio | Importe
    if (effective.length === 5) {
      const hasRun = this.isSizeRunName(effective[1])
      const thirdIsQty = this.looksLikeQuantity(effective[2])

      if (!hasRun && thirdIsQty) {
        values[cols.indexOf("product_code")] = effective[0]
        values[cols.indexOf("supplier_description_raw")] = effective[1]
        values[cols.indexOf("quantity")] = effective[2]
        values[cols.indexOf("unit_price")] = effective[3]
        values[cols.indexOf("line_total")] = effective[4]
      } else {
        values[cols.indexOf("supplier_description_raw")] = effective[0]
        values[cols.indexOf("size_run_id")] = effective[1]
        values[cols.indexOf("quantity")] = effective[2]
        values[cols.indexOf("unit_price")] = effective[3]
        values[cols.indexOf("line_total")] = effective[4]
      }
      return values
    }

    // Fallback: map by index as-is
    for (let i = 0; i < Math.min(effective.length, values.length); i++) {
      values[i] = effective[i]
    }
    return values
  }

  addEmptyRows(count) {
    for (let i = 0; i < count; i++) this.addRow([])
  }

  addRow(values = []) {
    const tr = document.createElement("tr")

    // per-row state
    tr.dataset.createNew = "0"
    tr.dataset.costDecision = ""
    tr.dataset.sizes = "{}"

    const columns = Array.isArray(this.columnsValue) ? this.columnsValue : []
    columns.forEach((column, idx) => {
      const td = document.createElement("td")
      td.dataset.field = column.field
      td.dataset.required = column.required || false

      if (column.field === "size_run_id") {
        const select = document.createElement("select")
        select.className = "form-control"
        select.innerHTML = `<option value="">Selecciona</option>` +
          (Array.isArray(this.sizeRunsValue) ? this.sizeRunsValue : []).map(sr => `<option value="${sr.id}">${sr.name}</option>`).join("")
        if (values[idx]) {
          // best-effort: try match by name
          const byName = (Array.isArray(this.sizeRunsValue) ? this.sizeRunsValue : []).find(sr => sr.name.toLowerCase() === String(values[idx]).trim().toLowerCase())
          if (byName) select.value = String(byName.id)
        }
        select.addEventListener("change", () => this.onCellChange())
        td.appendChild(select)
      } else if (column.field === "product_code") {
        const input = document.createElement("input")
        input.type = "text"
        input.className = "form-control"
        input.setAttribute("list", "purchase_product_suggestions")
        input.value = values[idx] || ""
        input.placeholder = "Código"
        input.addEventListener("input", () => {
          this.onCellChange()
          this.fetchProductSuggestions(input.value)
        })
        input.addEventListener("focus", () => this.fetchProductSuggestions(input.value))
        td.appendChild(input)
      } else {
        td.contentEditable = "true"
        td.textContent = values[idx] || ""
        td.addEventListener("input", () => this.onCellChange())
      }

      tr.appendChild(td)
    })

    const actionTd = document.createElement("td")
    actionTd.className = "import-table__actions"
    actionTd.innerHTML = this.renderActions(tr)
    tr.appendChild(actionTd)

    this.tableBodyTarget.appendChild(tr)
  }

  renderActions(tr, previewRow = null) {
    if (this.isRowEmpty(tr)) return ""

    const status = previewRow?.__status || ""
    const errors = Array.isArray(previewRow?.__errors) ? previewRow.__errors : []
    const warnings = Array.isArray(previewRow?.__warnings) ? previewRow.__warnings : []
    const matchedCode = previewRow?.__matched_product_code || ""
    const candidates = Array.isArray(previewRow?.__candidates) ? previewRow.__candidates : []
    const costCurrent = previewRow?.__cost_current
    const costChanged = previewRow?.__cost_changed
    const unitPrice = previewRow?.unit_price
    const currentDecision = tr.dataset.costDecision || ""
    const isCreatingNew = tr.dataset.createNew === "1"
    const sizeRunId = this.getCellValue(tr, "size_run_id")
    const qty = this.toInt(this.getCellValue(tr, "quantity"))
    const sizes = this.getSizesForRun(sizeRunId)
    const productCode = this.getCellValue(tr, "product_code")

    let html = `<div class="import-actions">`

    // =========================================================================
    // CASE 1: Product found (exact_code or matched_description)
    // =========================================================================
    if ((status === "exact_code" || status === "matched_description") && !isCreatingNew) {
      html += `<div class="import-actions__status text-success">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        Producto encontrado: <strong>${this.escape(matchedCode)}</strong>
      </div>`

      // Show size allocation if corrida is selected
      if (sizeRunId && qty && qty > 0 && sizes && sizes.length > 0) {
        html += this.renderSizeAllocation(tr, qty, sizes)
      } else if (!sizeRunId) {
        html += `<div class="import-actions__warning">Selecciona una corrida</div>`
      }

      // Option to create new product instead
      html += `<div class="import-actions__new-product" style="margin-top:0.5rem;">
        <label class="import-actions__checkbox">
          <input type="checkbox" data-action="change->purchase-import#toggleCreateNew">
          <span>No es este producto, crear nuevo</span>
        </label>
      </div>`
    }

    // =========================================================================
    // CASE 1b: Cost changed (product found but different price)
    // =========================================================================
    else if (status === "cost_changed" && !isCreatingNew) {
      const isManualSelect = tr.dataset.manualSelect === "1"
      const radioName = `cost_action_${tr.rowIndex}`

      html += `<div class="import-actions__status text-warning">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        Costo diferente: <strong>${this.escape(matchedCode)}</strong>
      </div>`

      // Cost change options
      const pctChange = costCurrent > 0 ? (((unitPrice - costCurrent) / costCurrent) * 100).toFixed(1) : 0
      const changeClass = unitPrice > costCurrent ? "text-danger" : "text-success"
      const changeSign = unitPrice > costCurrent ? "+" : ""

      html += `<div class="import-actions__cost-change">
        <div class="import-actions__cost-comparison">
          <span>Anterior: <strong>$${this.formatNumber(costCurrent)}</strong></span>
          <span>→</span>
          <span>Nuevo: <strong class="${changeClass}">$${this.formatNumber(unitPrice)}</strong></span>
          <span class="${changeClass}">(${changeSign}${pctChange}%)</span>
        </div>
        <div class="import-actions__cost-options">
          <label class="import-actions__radio ${currentDecision === 'keep' ? 'is-selected' : ''}">
            <input type="radio" name="cost_${tr.rowIndex}" value="keep" ${currentDecision === 'keep' ? 'checked' : ''} data-action="change->purchase-import#onCostDecision">
            <span>Mantener anterior</span>
          </label>
          <label class="import-actions__radio ${currentDecision === 'update_single' ? 'is-selected' : ''}">
            <input type="radio" name="cost_${tr.rowIndex}" value="update_single" ${currentDecision === 'update_single' ? 'checked' : ''} data-action="change->purchase-import#onCostDecision">
            <span>Actualizar este</span>
          </label>
          <label class="import-actions__radio ${currentDecision === 'update_model' ? 'is-selected' : ''}">
            <input type="radio" name="cost_${tr.rowIndex}" value="update_model" ${currentDecision === 'update_model' ? 'checked' : ''} data-action="change->purchase-import#onCostDecision">
            <span>Actualizar modelo (${changeSign}${pctChange}%)</span>
          </label>
        </div>
      </div>`

      // Show model cost preview if update_model is selected
      if (currentDecision === "update_model") {
        html += this.renderModelCostPreview(tr)
      }

      // Show size allocation if corrida is selected AND cost decision is made
      if (currentDecision && sizeRunId && qty && qty > 0 && sizes && sizes.length > 0) {
        html += this.renderSizeAllocation(tr, qty, sizes)
      } else if (!currentDecision) {
        html += `<div class="import-actions__error">Elige qué hacer con el costo</div>`
      } else if (!sizeRunId) {
        html += `<div class="import-actions__warning">Selecciona una corrida</div>`
      }

      // Product selection options (radio buttons)
      html += `<div class="import-actions__product-options">
        <label class="import-actions__radio ${!isManualSelect ? 'is-selected' : ''}">
          <input type="radio" name="${radioName}" value="accept" ${!isManualSelect ? 'checked' : ''} data-action="change->purchase-import#setCostProductMode">
          <span>Este es el producto correcto</span>
        </label>
        <label class="import-actions__radio ${isManualSelect ? 'is-selected' : ''}">
          <input type="radio" name="${radioName}" value="manual" ${isManualSelect ? 'checked' : ''} data-action="change->purchase-import#setCostProductMode">
          <span>Existe pero no está en la lista</span>
        </label>
        <label class="import-actions__radio">
          <input type="radio" name="${radioName}" value="new" data-action="change->purchase-import#setCostProductMode">
          <span>Crear producto nuevo</span>
        </label>
      </div>`

      if (isManualSelect) {
        // Check if product code is entered and corrida is selected
        const manualProductCode = this.getCellValue(tr, "product_code")
        const manualSizeRunId = this.getCellValue(tr, "size_run_id")
        
        if (manualProductCode && manualSizeRunId) {
          // Get sizes for the selected corrida
          const manualSizes = this.getSizesForRun(manualSizeRunId)
          if (manualSizes && manualSizes.length > 0) {
            html += this.renderSizeAllocation(tr, qty, manualSizes)
          } else {
            html += `<div class="import-actions__hint">Escribe el código en PRODUCTO y selecciona CORRIDA</div>`
          }
        } else {
          html += `<div class="import-actions__hint">Escribe el código en PRODUCTO y selecciona CORRIDA</div>`
        }
      }
    }

    // =========================================================================
    // CASE 2: Ambiguous - multiple possible matches
    // =========================================================================
    else if ((status === "ambiguous" && candidates.length > 0) && !isCreatingNew) {
      const isManualSelect = tr.dataset.manualSelect === "1"
      const radioName = `ambiguous_action_${tr.rowIndex}`

      html += `<div class="import-actions__status text-warning">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        ${candidates.length} coincidencias encontradas
      </div>`

      // Radio: Select from list (default)
      html += `<div class="import-actions__ambiguous-options">
        <label class="import-actions__radio ${!isManualSelect ? 'is-selected' : ''}">
          <input type="radio" name="${radioName}" value="select" ${!isManualSelect ? 'checked' : ''} data-action="change->purchase-import#setAmbiguousMode">
          <span>Seleccionar de la lista:</span>
        </label>`

      // Dropdown for candidates (only enabled when select mode)
      html += `<div class="import-actions__candidates ${isManualSelect ? 'is-disabled' : ''}">
        <select class="form-control form-control--sm" data-action="change->purchase-import#selectCandidate" ${isManualSelect ? 'disabled' : ''}>
          <option value="">-- Selecciona el producto --</option>
          ${candidates.map(c => `<option value="${this.escapeAttr(c.code)}" data-size-run="${c.size_run_id || ''}">${this.escape(c.code)} - ${this.escape(c.description)} (${c.score}%)</option>`).join("")}
        </select>
      </div>`

      // Radio: Product exists but not in list
      html += `<label class="import-actions__radio ${isManualSelect ? 'is-selected' : ''}">
          <input type="radio" name="${radioName}" value="manual" ${isManualSelect ? 'checked' : ''} data-action="change->purchase-import#setAmbiguousMode">
          <span>Existe pero no está en la lista</span>
        </label>`

      if (isManualSelect) {
        // Check if product code is entered and corrida is selected
        const manualProductCode = this.getCellValue(tr, "product_code")
        const manualSizeRunId = this.getCellValue(tr, "size_run_id")
        
        if (manualProductCode && manualSizeRunId) {
          // Get sizes for the selected corrida
          const manualSizes = this.getSizesForRun(manualSizeRunId)
          if (manualSizes && manualSizes.length > 0) {
            html += this.renderSizeAllocation(tr, qty, manualSizes)
          } else {
            html += `<div class="import-actions__hint">Escribe el código en PRODUCTO y selecciona CORRIDA</div>`
          }
        } else {
          html += `<div class="import-actions__hint">Escribe el código en PRODUCTO y selecciona CORRIDA</div>`
        }
      }

      // Radio: Create new product
      html += `<label class="import-actions__radio">
          <input type="radio" name="${radioName}" value="new" data-action="change->purchase-import#setAmbiguousMode">
          <span>Crear producto nuevo</span>
        </label>
      </div>`
    }

    // =========================================================================
    // CASE 3: Not found OR creating new (even from ambiguous)
    // =========================================================================
    else if (status === "not_found" || isCreatingNew) {
      const createdProductId = tr.dataset.createdProductId
      const createdProductCode = tr.dataset.createdProductCode
      const hasCreatedProduct = createdProductId && createdProductId !== ""

      if (!isCreatingNew) {
        // Not creating new yet - show option to create
        html += `<div class="import-actions__status text-danger">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          Producto no encontrado
        </div>`

        html += `<div class="import-actions__new-product">
          <label class="import-actions__checkbox">
            <input type="checkbox" data-action="change->purchase-import#toggleCreateNew">
            <span>Crear producto nuevo</span>
          </label>
        </div>`
      } else if (hasCreatedProduct) {
        // Product was created - show success state with edit option
        html += `<div class="import-actions__status text-success">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Producto creado: <strong>${this.escape(createdProductCode)}</strong>
        </div>`

        html += `<div class="import-actions__product-actions">
          <button type="button" class="btn btn-secondary btn-sm" data-action="click->purchase-import#editCreatedProduct">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Editar
          </button>
          <button type="button" class="btn btn-link btn-sm text-muted" style="padding:0; font-size:0.7rem;" data-action="click->purchase-import#toggleCreateNew">
            Cancelar
          </button>
        </div>`

        // Show size allocation if corrida is selected
        if (sizeRunId && qty && qty > 0 && sizes && sizes.length > 0) {
          html += this.renderSizeAllocation(tr, qty, sizes)
        } else if (!sizeRunId) {
          html += `<div class="import-actions__warning">Selecciona una corrida</div>`
        }
      } else {
        // Creating new - show button to open modal
        html += `<div class="import-actions__status text-primary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Producto nuevo
          <button type="button" class="btn btn-link btn-sm text-muted" style="padding:0; margin-left:0.5rem; font-size:0.7rem;" data-action="click->purchase-import#toggleCreateNew">Cancelar</button>
        </div>`

        html += `<div class="import-actions__create-product">
          <button type="button" class="btn btn-primary btn-sm" data-action="click->purchase-import#openCreateProductModal">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Crear Producto
          </button>
        </div>`
      }
    }

    // =========================================================================
    // Fallback: No status yet (before processing)
    // =========================================================================
    else if (!status) {
      html += `<div class="import-actions__status text-muted">-</div>`
    }

    // Show first error if any (not already shown)
    if (errors.length > 0 && status !== "not_found" && status !== "cost_changed") {
      const shownErrors = ["Producto no encontrado", "Costo diferente"]
      const remainingErrors = errors.filter(e => !shownErrors.some(s => e.includes(s)))
      if (remainingErrors.length > 0) {
        html += `<div class="import-actions__error">${this.escape(remainingErrors[0])}</div>`
      }
    }

    // Show warnings
    if (warnings.length > 0 && status !== "ambiguous") {
      html += `<div class="import-actions__warning">${this.escape(warnings[0])}</div>`
    }

    html += `</div>`
    return html
  }

  renderModelCostPreview(tr) {
    const previewData = tr.dataset.modelCostPreview
    if (!previewData) {
      return `<div class="model-cost-preview model-cost-preview--loading">
        <div class="spinner-small"></div>
        <span>Cargando productos afectados...</span>
      </div>`
    }

    let preview
    try {
      preview = JSON.parse(previewData)
    } catch {
      return `<div class="model-cost-preview model-cost-preview--error">Error al cargar preview</div>`
    }

    if (preview.error) {
      return `<div class="model-cost-preview model-cost-preview--error">${this.escape(preview.error)}</div>`
    }

    if (!preview.products || preview.products.length === 0) {
      return `<div class="model-cost-preview model-cost-preview--empty">No hay otros productos del mismo modelo</div>`
    }

    const pctChange = preview.percentage_change
    const changeClass = pctChange > 0 ? "text-danger" : "text-success"
    const changeSign = pctChange > 0 ? "+" : ""
    const isConfirmed = tr.dataset.modelCostConfirmed === "1"
    const currentNewPrice = tr.dataset.modelNewPrice || ""
    const priceMode = tr.dataset.priceMode || "proportional"
    const hasMultipleSizeRuns = preview.has_multiple_size_runs
    const suggestedProportionalPrice = preview.suggested_proportional_price || ""
    const sourceCurrentPrice = preview.source_current_price || 0

    // Group products by size_run for display
    const byRun = {}
    preview.products.forEach(p => {
      const run = p.size_run || "Sin corrida"
      if (!byRun[run]) byRun[run] = []
      byRun[run].push(p)
    })

    let html = `<div class="model-cost-preview">
      <div class="model-cost-preview__header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        <span><strong>${preview.products.length}</strong> productos cambiarán de costo (<span class="${changeClass}">${changeSign}${pctChange}%</span>)</span>
      </div>`

    // Show products grouped by size run if there are multiple
    if (hasMultipleSizeRuns) {
      html += `<div class="model-cost-preview__runs-notice">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>Hay <strong>${Object.keys(byRun).length}</strong> corridas diferentes en este modelo</span>
      </div>`
    }

    html += `<div class="model-cost-preview__list">`

    // Show products grouped
    Object.entries(byRun).forEach(([runName, products]) => {
      if (hasMultipleSizeRuns && Object.keys(byRun).length > 1) {
        const runPrices = [...new Set(products.map(p => p.current_sale_price).filter(Boolean))]
        const priceInfo = runPrices.length > 0 ? `Precio actual: $${runPrices.map(p => this.formatNumber(p)).join(', $')}` : ''
        html += `<div class="model-cost-preview__run-header">
          <span class="model-cost-preview__run-name">${this.escape(runName)}</span>
          <span class="model-cost-preview__run-count">${products.length} producto${products.length > 1 ? 's' : ''}</span>
          ${priceInfo ? `<span class="model-cost-preview__run-price">${priceInfo}</span>` : ''}
        </div>`
      }

      products.forEach(p => {
        const isSource = p.is_source
        html += `<div class="model-cost-preview__item ${isSource ? 'model-cost-preview__item--source' : ''}">
          <div class="model-cost-preview__item-info">
            <span class="model-cost-preview__item-code">${this.escape(p.code)}</span>
            ${isSource ? '<span class="model-cost-preview__item-badge">← este</span>' : ''}
            <span class="model-cost-preview__item-meta">${this.escape(p.color || '')}${!hasMultipleSizeRuns ? ' ' + this.escape(p.size_run || '') : ''}</span>
          </div>
          <div class="model-cost-preview__item-prices">
            <span class="model-cost-preview__item-old">$${this.formatNumber(p.old_cost)}</span>
            <span>→</span>
            <span class="model-cost-preview__item-new ${changeClass}">$${this.formatNumber(p.new_cost)}</span>
          </div>
        </div>`
      })
    })

    html += `</div>
      <div class="model-cost-preview__price-update">
        <label class="model-cost-preview__price-label">
          <input type="checkbox" ${currentNewPrice ? 'checked' : ''} data-action="change->purchase-import#onToggleNewPrice">
          <span>También actualizar precio de venta</span>
        </label>
        <div class="model-cost-preview__price-options ${currentNewPrice ? '' : 'hidden'}">`

    // If multiple size runs with different prices, show options
    if (hasMultipleSizeRuns) {
      html += `
          <div class="model-cost-preview__price-mode">
            <label class="model-cost-preview__price-mode-option">
              <input type="radio" name="price_mode_${tr.rowIndex}" value="proportional" 
                     ${priceMode === 'proportional' ? 'checked' : ''} 
                     data-action="change->purchase-import#onPriceModeChange">
              <span>Proporcional (${changeSign}${pctChange}%)</span>
              <small>Cada corrida mantiene su diferencia de precio</small>
            </label>
            <label class="model-cost-preview__price-mode-option">
              <input type="radio" name="price_mode_${tr.rowIndex}" value="fixed" 
                     ${priceMode === 'fixed' ? 'checked' : ''} 
                     data-action="change->purchase-import#onPriceModeChange">
              <span>Precio fijo</span>
              <small>Mismo precio para todas las corridas</small>
            </label>
          </div>`
    }

    const showFixedInput = !hasMultipleSizeRuns || priceMode === 'fixed'
    const suggestedFixed = preview.products[0]?.suggested_sale_price || ""

    html += `
          <div class="model-cost-preview__price-input ${showFixedInput ? '' : 'hidden'}" data-price-input="fixed">
            <span class="input-prefix">$</span>
            <input type="number" step="0.01" min="0" value="${currentNewPrice || suggestedFixed}" 
                   placeholder="${suggestedFixed}" data-action="input->purchase-import#onNewPriceChange">
            <span class="model-cost-preview__price-hint">Sugerido: $${this.formatNumber(suggestedFixed)}</span>
          </div>
          <div class="model-cost-preview__price-proportional ${!showFixedInput ? '' : 'hidden'}" data-price-input="proportional">
            <span class="model-cost-preview__price-proportional-text">
              Los precios se incrementarán <strong>${changeSign}${pctChange}%</strong>
            </span>
            ${sourceCurrentPrice > 0 ? `<small>Ej: $${this.formatNumber(sourceCurrentPrice)} → $${this.formatNumber(suggestedProportionalPrice)}</small>` : ''}
          </div>
        </div>
      </div>
      <label class="model-cost-preview__confirm">
        <input type="checkbox" ${isConfirmed ? 'checked' : ''} data-action="change->purchase-import#onModelCostConfirm">
        <span>Confirmo actualizar ${preview.products.length} producto${preview.products.length > 1 ? 's' : ''}</span>
      </label>
    </div>`
    return html
  }

  renderSizeAllocation(tr, qty, sizes) {
    const alloc = this.getRowSizes(tr)
    const allocSum = this.sumAlloc(alloc)
    const isComplete = allocSum === qty

    let html = `<div class="import-actions__sizes">
      <div class="import-actions__sizes-header">
        <span>Asignar tallas</span>
        <span class="${isComplete ? 'text-success' : 'text-warning'}">${allocSum}/${qty}</span>
      </div>
      <div class="import-actions__sizes-grid">`

    sizes.forEach(size => {
      const current = alloc[size] || 0
      html += `<div class="import-actions__size-row">
        <span class="import-actions__size-label">${this.escape(size)}</span>
        <div class="import-actions__size-controls">
          <button type="button" class="btn btn-secondary btn-sm import-actions__size-btn" data-action="click->purchase-import#decSize" data-size="${this.escapeAttr(size)}">−</button>
          <span class="import-actions__size-value">${current}</span>
          <button type="button" class="btn btn-secondary btn-sm import-actions__size-btn" data-action="click->purchase-import#incSize" data-size="${this.escapeAttr(size)}">+</button>
        </div>
      </div>`
    })

    html += `</div></div>`
    return html
  }

  formatNumber(n) {
    if (n === null || n === undefined) return "0.00"
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  selectCandidate(event) {
    const tr = event.target.closest("tr")
    if (!tr) return

    const select = event.target
    const code = select.value
    const selectedOption = select.options[select.selectedIndex]
    const sizeRunId = selectedOption?.dataset?.sizeRun

    // Update product code input
    const productTd = tr.querySelector('td[data-field="product_code"]')
    const productInput = productTd?.querySelector('input')
    if (productInput) {
      productInput.value = code
    }

    // Update size run select if available
    if (sizeRunId) {
      const sizeRunTd = tr.querySelector('td[data-field="size_run_id"]')
      const sizeRunSelect = sizeRunTd?.querySelector('select')
      if (sizeRunSelect) {
        sizeRunSelect.value = sizeRunId
      }
    }

    // Auto-run preview after selection
    this.runAutoPreview()
  }

  onCostDecision(event) {
    const tr = event.target.closest("tr")
    if (!tr) return

    const decision = event.target.value
    tr.dataset.costDecision = decision

    // If update_model is selected, load the preview of affected products
    if (decision === "update_model") {
      tr.dataset.modelCostConfirmed = "0" // Reset confirmation when changing decision
      tr.dataset.modelNewPrice = ""
      this.loadModelCostPreview(tr)
    } else {
      // Clear the preview if another option is selected
      tr.dataset.modelCostPreview = ""
      tr.dataset.modelCostConfirmed = ""
      tr.dataset.modelNewPrice = ""
      // Auto-run preview when keep or update_single selected
      this.runAutoPreview()
    }

    this.refreshRowActions(tr)
  }

  onModelCostConfirm(event) {
    const tr = event.target.closest("tr")
    if (!tr) return

    tr.dataset.modelCostConfirmed = event.target.checked ? "1" : "0"
    
    // Auto-run preview when confirmed
    if (event.target.checked) {
      this.runAutoPreview()
    }
  }

  onToggleNewPrice(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    
    const priceOptions = tr.querySelector(".model-cost-preview__price-options")
    const priceInputFixed = tr.querySelector("[data-price-input='fixed']")
    const priceInputProportional = tr.querySelector("[data-price-input='proportional']")
    const priceInput = priceInputFixed?.querySelector("input[type='number']")
    
    if (event.target.checked) {
      priceOptions?.classList.remove("hidden")
      // Set default mode to proportional if there are multiple size runs
      if (!tr.dataset.priceMode) {
        tr.dataset.priceMode = "proportional"
      }
      if (tr.dataset.priceMode === "fixed" && priceInput && !tr.dataset.modelNewPrice) {
        tr.dataset.modelNewPrice = priceInput.value
      } else if (tr.dataset.priceMode === "proportional") {
        tr.dataset.modelNewPrice = "proportional"
      }
    } else {
      priceOptions?.classList.add("hidden")
      tr.dataset.modelNewPrice = ""
      tr.dataset.priceMode = ""
    }
  }

  onPriceModeChange(event) {
    const tr = event.target.closest("tr")
    if (!tr) return

    const mode = event.target.value
    tr.dataset.priceMode = mode

    const priceInputFixed = tr.querySelector("[data-price-input='fixed']")
    const priceInputProportional = tr.querySelector("[data-price-input='proportional']")
    const priceInput = priceInputFixed?.querySelector("input[type='number']")

    if (mode === "fixed") {
      priceInputFixed?.classList.remove("hidden")
      priceInputProportional?.classList.add("hidden")
      if (priceInput) {
        tr.dataset.modelNewPrice = priceInput.value
      }
    } else {
      priceInputFixed?.classList.add("hidden")
      priceInputProportional?.classList.remove("hidden")
      tr.dataset.modelNewPrice = "proportional"
    }
  }

  onNewPriceChange(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    
    tr.dataset.modelNewPrice = event.target.value
  }

  // Debounced auto-preview to avoid too many requests
  runAutoPreview() {
    if (this.autoPreviewTimeout) {
      clearTimeout(this.autoPreviewTimeout)
    }
    this.autoPreviewTimeout = setTimeout(() => {
      this.processPreview()
    }, 300)
  }

  async loadModelCostPreview(tr) {
    // Get product ID from stored preview row data
    let productId = null
    try {
      const previewRow = tr.dataset.previewRow ? JSON.parse(tr.dataset.previewRow) : null
      productId = previewRow?.__matched_product_id
    } catch {
      productId = null
    }

    const supplierId = this.supplierSelectTarget.value
    const newPrice = this.parseDecimal(this.getCellValue(tr, "unit_price"))

    if (!productId || !supplierId || !newPrice) {
      tr.dataset.modelCostPreview = JSON.stringify({ error: "Datos insuficientes" })
      this.refreshRowActions(tr)
      return
    }

    try {
      const url = `${this.modelCostPreviewUrlValue}?product_id=${productId}&supplier_id=${supplierId}&new_price=${newPrice}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      tr.dataset.modelCostPreview = JSON.stringify(data)
      this.refreshRowActions(tr)
    } catch (error) {
      console.error("Error loading model cost preview:", error)
      tr.dataset.modelCostPreview = JSON.stringify({ error: "Error al cargar preview" })
      this.refreshRowActions(tr)
    }
  }

  onCellChange() {
    // reset decisions when user edits
    this.updateCounts()
    this.updateTotalAmount()
    this.previewValidCount = 0
    this.previewErrorCount = 0

    // re-render allocations when corrida/cantidad changes
    const tr = this.eventRowFromActiveElement()
    if (tr) {
      this.refreshRowActions(tr)
      // Update validation state for manual mode
      this.updateRowValidationState(tr)
    }
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("file", file)

    fetch(this.parseUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          toast.error(data.error)
          return
        }

        this.tableBodyTarget.innerHTML = ""
        data.rows.forEach(row => {
          const values = this.columnsValue.map(col => {
            if (col.field === "size_run_id") return row.size_run_id || row.size_run_name || ""
            return row[col.field] ?? ""
          })
          this.addRow(values)
        })
        this.addEmptyRows(3)
        this.updateCounts()
        this.updateTotalAmount()
        this.previewValidCount = 0
        this.previewErrorCount = 0
      })
      .catch(err => {
        console.error(err)
        toast.error("Error al procesar el archivo")
      })
      .finally(() => {
        this.fileInputTarget.value = ""
      })
  }

  onSupplierChange() {
    this.previewValidCount = 0
    this.previewErrorCount = 0
    this.updateImportButtonState()
  }

  processPreview() {
    this.autoImportAfterPreview = false

    const supplierId = this.supplierSelectTarget.value
    if (!supplierId) {
      toast.error("Selecciona un proveedor")
      return
    }

    const rows = this.getRowsData()
    if (rows.length === 0) {
      toast.error("No hay filas para procesar")
      return
    }

    const formData = new FormData()
    formData.set("supplier_id", supplierId)
    formData.set("order_number", this.orderNumberTarget.value || "")
    formData.set("rows_data", JSON.stringify(rows))

    this.previewButtonTarget.disabled = true
    this.showProgress()
    this.inFlight = true
    this.importButtonTarget.disabled = true

    fetch(this.previewUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        if (data.async) {
          this.importTaskId = data.import_task_id
          this.startPolling()
        }
      })
      .catch(err => {
        console.error(err)
        toast.error("Error al procesar")
      })
      .finally(() => {
        this.previewButtonTarget.disabled = false
        this.inFlight = false
        this.updateImportButtonState()
      })
  }

  processImport() {
    // Always run a fresh preview before importing to ensure rows_data is up to date
    // with user decisions (cost decisions, confirmations, etc.)
    this.autoImportAfterPreview = true
    this.processPreview()
  }

  startImportRequest() {
    const formData = new FormData()
    formData.set("import_task_id", String(this.importTaskId))

    this.inFlight = true
    this.importButtonTarget.disabled = true
    this.showProgress()

    fetch(this.importUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        if (data.async) {
          this.importTaskId = data.import_task_id
          this.startPolling()
        }
      })
      .catch(err => {
        console.error(err)
        toast.error("Error al importar")
      })
      .finally(() => {
        this.inFlight = false
        this.updateImportButtonState()
      })
  }

  startPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer)

    this.pollTimer = setInterval(() => this.checkStatus(), this.pollIntervalValue)
    this.checkStatus()
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  checkStatus() {
    if (!this.importTaskId) return

    const url = this.statusUrlTemplateValue.replace("__ID__", String(this.importTaskId))

    fetch(url, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => {
        this.updateProgress(data.progress || 0, `Procesando ${data.processed_rows} de ${data.total_rows}...`)

        const isComplete = data.status === "completed" || data.status === "failed"

        if (Array.isArray(data.rows_data)) {
          // During processing, only apply results quietly (no toast, minimal re-render)
          // When complete/failed, do full apply with toast
          this.applyPreviewResults(data.rows_data, isComplete)
        }

        if (isComplete) {
          this.stopPolling()
          this.hideProgress()

          // Check for job-level errors
          if (data.status === "failed") {
            const jobErrors = Array.isArray(data.errors) ? data.errors : []
            if (jobErrors.length > 0) {
              toast.error(jobErrors[0]?.message || "Error al procesar")
            } else {
              toast.error("Error al procesar el archivo")
            }
            return
          }

          const phase = data?.metadata?.phase
          
          // Handle successful import completion
          if (data.status === "completed" && phase === "import") {
            const purchaseId = data?.metadata?.purchase_id
            const totalItems = data.successful_rows || 0
            toast.success(`¡Compra realizada! Se importaron ${totalItems} productos`)
            this.resetAfterSuccessfulImport()
            return
          }
          
          if (data.status === "completed" && phase === "preview" && this.autoImportAfterPreview) {
            this.autoImportAfterPreview = false

            if (this.previewValidCount > 0 && this.previewErrorCount === 0) {
              this.startImportRequest()
            } else if (this.previewErrorCount > 0) {
              toast.error("No se puede importar: hay filas con errores")
            }
          }
        }
      })
      .catch(err => {
        console.error(err)
        this.stopPolling()
        this.hideProgress()
      })
  }

  applyPreviewResults(rowsData, showToast = true) {
    const trs = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    let valid = 0
    let errors = 0

    const byClientIndex = new Map()
    rowsData.forEach(r => {
      const key = r?.__client_row_index
      if (key !== undefined && key !== null) byClientIndex.set(Number(key), r)
    })

    trs.forEach((tr, idx) => {
      const row = byClientIndex.get(idx)
      const actionTd = tr.querySelector("td.import-table__actions")
      if (!row || !actionTd) return

      // Check if this row's data actually changed
      const previousData = tr.dataset.previewRow
      const newData = JSON.stringify(row)
      const dataChanged = previousData !== newData

      // Store preview row data in tr for later use when refreshing
      tr.dataset.previewRow = newData

      // Update product_code input if we found a match (only if empty)
      if (row.__matched_product_code) {
        const productTd = tr.querySelector('td[data-field="product_code"]')
        const productInput = productTd?.querySelector('input')
        if (productInput && !productInput.value) {
          productInput.value = row.__matched_product_code
        }
      }

      // Update size_run select if product has one (only if empty)
      if (row.size_run_id) {
        const sizeRunTd = tr.querySelector('td[data-field="size_run_id"]')
        const sizeRunSelect = sizeRunTd?.querySelector('select')
        if (sizeRunSelect && !sizeRunSelect.value) {
          sizeRunSelect.value = String(row.size_run_id)
        }
      }

      const rowErrors = row.__errors || []
      const hadErrors = tr.classList.contains("row-error")
      const hasErrors = rowErrors.length > 0

      if (hasErrors) {
        tr.classList.add("row-error")
        tr.classList.remove("row-duplicate-import")
        errors++
      } else {
        tr.classList.remove("row-error")
        if ((row.__warnings || []).length > 0) {
          tr.classList.add("row-duplicate-import")
        } else {
          tr.classList.remove("row-duplicate-import")
        }
        valid++
      }

      // Only re-render actions if data changed or error state changed
      if (dataChanged || hadErrors !== hasErrors) {
        this.smoothUpdateActions(actionTd, tr, row)
      }
    })

    if (this.hasValidCountTarget) this.validCountTarget.textContent = String(valid)
    if (this.hasErrorCountTarget) this.errorCountTarget.textContent = String(errors)

    this.previewValidCount = valid
    this.previewErrorCount = errors

    this.updateTotalAmount()

    // Update import button state based on all conditions
    if (!this.inFlight) {
      this.updateImportButtonState()
    }

    // Only show toast when explicitly requested (at the end of processing)
    if (showToast) {
      if (errors > 0) {
        toast.warning(`Hay ${errors} filas con errores`)
      } else if (valid > 0) {
        toast.success("Documento listo para importar")
      }
    }
  }

  // Smooth update of actions cell to avoid jarring visual jumps
  smoothUpdateActions(actionTd, tr, row) {
    const newContent = this.renderActions(tr, row)
    
    // If content is the same, don't update
    if (actionTd.innerHTML.trim() === newContent.trim()) return
    
    // Add transition class
    actionTd.classList.add("updating")
    
    // Use requestAnimationFrame for smoother update
    requestAnimationFrame(() => {
      actionTd.innerHTML = newContent
      // Remove transition class after a short delay
      setTimeout(() => {
        actionTd.classList.remove("updating")
      }, 150)
    })
  }

  getRowsData() {
    const trs = Array.from(this.tableBodyTarget.querySelectorAll("tr"))

    return trs
      .map((tr, clientIndex) => {
        const tds = Array.from(tr.querySelectorAll("td[data-field]"))
        const row = {}

        tds.forEach(td => {
          const field = td.dataset.field
          if (field === "size_run_id") {
            const select = td.querySelector("select")
            const value = select?.value
            if (value) row[field] = value
          } else if (field === "product_code") {
            const input = td.querySelector("input")
            const value = input?.value?.trim()
            if (value) row[field] = value
          } else {
            const value = td.textContent.trim()
            if (value) row[field] = value
          }
        })

        // include per-row decisions
        if (tr.dataset.createNew === "1") row.__create_new_product = true
        if (tr.dataset.costDecision) row.__cost_decision = tr.dataset.costDecision
        if (tr.dataset.modelCostConfirmed === "1") row.__model_cost_confirmed = true
        if (tr.dataset.modelNewPrice) row.__model_new_price = parseFloat(tr.dataset.modelNewPrice)
        if (tr.dataset.manualSelect === "1") row.__manual_select = true
        const sizes = this.getRowSizes(tr)
        if (Object.keys(sizes).length > 0) row._sizes = sizes

        // Include matched product ID from previous preview (important for re-preview)
        // But NOT if manual select - user wants to use their own code
        if (tr.dataset.manualSelect !== "1") {
          try {
            const previewRow = tr.dataset.previewRow ? JSON.parse(tr.dataset.previewRow) : null
            if (previewRow?.__matched_product_id) {
              row.__matched_product_id = previewRow.__matched_product_id
            }
          } catch { /* ignore */ }
        }

        // ignore empty rows
        const hasContent = Object.keys(row).length > 0
        if (!hasContent) return null

        row.__client_row_index = clientIndex
        return row
      })
      .filter(Boolean)
  }

  updateCounts() {
    const trs = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    const nonEmpty = trs.filter(tr => !this.isRowEmpty(tr)).length
    if (this.hasRowCountTarget) this.rowCountTarget.textContent = String(nonEmpty)

    // If preview hasn't run, keep valid/error at 0 (preview will set them).
    if (this.previewValidCount === 0 && this.previewErrorCount === 0) {
      if (this.hasValidCountTarget) this.validCountTarget.textContent = "0"
      if (this.hasErrorCountTarget) this.errorCountTarget.textContent = "0"
    }
  }

  updateTotalAmount() {
    if (!this.hasTotalAmountTarget) return

    const trs = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    let total = 0

    trs.forEach(tr => {
      if (this.isRowEmpty(tr)) return

      const lineTotal = this.parseDecimal(this.getCellValue(tr, "line_total"))
      if (lineTotal !== null) {
        total += lineTotal
        return
      }

      const qty = this.parseDecimal(this.getCellValue(tr, "quantity"))
      const price = this.parseDecimal(this.getCellValue(tr, "unit_price"))
      if (qty !== null && price !== null) total += qty * price
    })

    this.totalAmountTarget.textContent = this.formatMoney(total)
  }

  formatMoney(amount) {
    const n = Number(amount)
    if (!Number.isFinite(n)) return "0.00"
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  parseDecimal(v) {
    const s0 = String(v ?? "").trim()
    if (s0 === "") return null
    let s = s0.replace(/[^0-9,\.\-]/g, "")
    if (s.includes(",") && s.includes(".")) {
      s = s.replace(/,/g, "")
    } else if (s.includes(",") && !s.includes(".")) {
      s = s.replace(/,/g, ".")
    }
    const n = Number.parseFloat(s)
    return Number.isFinite(n) ? n : null
  }

  looksLikeQuantity(value) {
    const n = this.parseDecimal(value)
    return n !== null
  }

  isSizeRunName(value) {
    const v = String(value ?? "").trim().toLowerCase()
    if (!v) return false
    return (Array.isArray(this.sizeRunsValue) ? this.sizeRunsValue : []).some(sr => String(sr.name).trim().toLowerCase() === v)
  }

  humanStatus(status) {
    const s = String(status ?? "").trim()
    if (s === "") return "-"
    const map = {
      exact_code: "Encontrado (código)",
      matched_description: "Encontrado (descripción)",
      cost_changed: "Costo diferente",
      not_found: "No encontrado",
      new: "Nuevo",
      ambiguous: "Ambiguo"
    }
    return map[s] || s
  }

  // Actions handlers
  setAmbiguousMode(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    
    const mode = event.target.value // 'select', 'manual', 'new'
    
    if (mode === "new") {
      tr.dataset.createNew = "1"
      tr.dataset.manualSelect = "0"
      toast.info("Marcado como producto nuevo")
    } else if (mode === "manual") {
      tr.dataset.createNew = "0"
      tr.dataset.manualSelect = "1"
      toast.info("Selecciona el producto manualmente en la columna PRODUCTO")
    } else {
      tr.dataset.createNew = "0"
      tr.dataset.manualSelect = "0"
    }
    
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  openCreateProductModal(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    this.showCreateProductModal(tr)
  }

  editCreatedProduct(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    this.showCreateProductModal(tr)
  }

  setCostProductMode(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    
    const mode = event.target.value // 'accept', 'manual', 'new'
    
    if (mode === "new") {
      tr.dataset.createNew = "1"
      tr.dataset.manualSelect = "0"
      toast.info("Marcado como producto nuevo")
    } else if (mode === "manual") {
      tr.dataset.createNew = "0"
      tr.dataset.manualSelect = "1"
      toast.info("Selecciona el producto manualmente en la columna PRODUCTO")
    } else {
      // accept - this is the correct product
      tr.dataset.createNew = "0"
      tr.dataset.manualSelect = "0"
    }
    
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  toggleCreateNew(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    tr.dataset.createNew = tr.dataset.createNew === "1" ? "0" : "1"
    tr.dataset.manualSelect = "0"
    toast.info(tr.dataset.createNew === "1" ? "Marcado como producto nuevo" : "Desmarcado producto nuevo")
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  setCostKeep(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    tr.dataset.costDecision = "keep"
    toast.info("Costo: mantener")
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  setCostUpdate(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    tr.dataset.costDecision = "update_single"
    toast.info("Costo: actualizar")
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  setCostUpdateModel(event) {
    const tr = event.target.closest("tr")
    if (!tr) return
    tr.dataset.costDecision = "update_model"
    toast.info("Costo: actualizar por modelo")
    this.refreshRowActions(tr)
    this.previewValidCount = 0
    this.previewErrorCount = 0
  }

  incSize(event) {
    const tr = event.target.closest("tr")
    const size = event.target.dataset.size
    if (!tr || !size) return

    const qty = this.toInt(this.getCellValue(tr, "quantity"))
    const alloc = this.getRowSizes(tr)
    const sum = this.sumAlloc(alloc)
    if (qty && sum >= qty) return

    alloc[size] = (alloc[size] || 0) + 1
    this.setRowSizes(tr, alloc)
    this.refreshRowActions(tr)
    
    // Update validation state for manual mode
    this.updateRowValidationState(tr)
  }

  decSize(event) {
    const tr = event.target.closest("tr")
    const size = event.target.dataset.size
    if (!tr || !size) return

    const alloc = this.getRowSizes(tr)
    alloc[size] = Math.max(0, (alloc[size] || 0) - 1)
    if (alloc[size] === 0) delete alloc[size]
    this.setRowSizes(tr, alloc)
    this.refreshRowActions(tr)
    
    // Update validation state for manual mode
    this.updateRowValidationState(tr)
  }

  // Local validation for manual mode - validates product code + corrida + sizes complete
  updateRowValidationState(tr) {
    const isManualSelect = tr.dataset.manualSelect === "1"
    if (!isManualSelect) return
    
    const productCode = this.getCellValue(tr, "product_code")
    const sizeRunId = this.getCellValue(tr, "size_run_id")
    const qty = this.toInt(this.getCellValue(tr, "quantity"))
    const alloc = this.getRowSizes(tr)
    const allocSum = this.sumAlloc(alloc)
    
    const isValid = productCode && sizeRunId && qty > 0 && allocSum === qty
    
    if (isValid) {
      tr.classList.remove("row-error")
      tr.classList.add("row-valid-manual")
    } else {
      tr.classList.remove("row-valid-manual")
    }
    
    // Update counts
    this.updateManualValidCounts()
  }

  // Update valid/error counts considering manual validations
  updateManualValidCounts() {
    const trs = Array.from(this.tableBodyTarget.querySelectorAll("tr"))
    let valid = 0
    let errors = 0
    
    trs.forEach(tr => {
      if (this.isRowEmpty(tr)) return
      
      const hasError = tr.classList.contains("row-error")
      const isManualValid = tr.classList.contains("row-valid-manual")
      
      // Check if row is validated by server preview
      const previewRow = tr.dataset.previewRow ? JSON.parse(tr.dataset.previewRow) : null
      const serverErrors = previewRow?.__errors || []
      
      if (isManualValid || (serverErrors.length === 0 && previewRow)) {
        valid++
      } else if (hasError || serverErrors.length > 0) {
        errors++
      }
    })
    
    if (this.hasValidCountTarget) this.validCountTarget.textContent = String(valid)
    if (this.hasErrorCountTarget) this.errorCountTarget.textContent = String(errors)
    
    this.previewValidCount = valid
    this.previewErrorCount = errors
    this.updateImportButtonState()
  }

  refreshRowActions(tr, smooth = false) {
    const actionTd = tr.querySelector("td.import-table__actions")
    if (!actionTd) return
    
    // Recover previewRow from stored data
    let previewRow = null
    try {
      previewRow = tr.dataset.previewRow ? JSON.parse(tr.dataset.previewRow) : null
    } catch (e) {
      previewRow = null
    }
    
    if (smooth) {
      this.smoothUpdateActions(actionTd, tr, previewRow)
    } else {
      actionTd.innerHTML = this.renderActions(tr, previewRow)
    }
  }

  isRowEmpty(tr) {
    const fields = this.columnsValue.map(c => c.field)
    for (const field of fields) {
      const val = this.getCellValue(tr, field)
      if (String(val || "").trim() !== "") return false
    }
    return true
  }

  // Helpers
  getCellValue(tr, field) {
    const td = tr.querySelector(`td[data-field="${field}"]`)
    if (!td) return ""
    if (field === "size_run_id") return td.querySelector("select")?.value || ""
    if (field === "product_code") return td.querySelector("input")?.value || ""
    return td.textContent.trim()
  }

  getSizesForRun(sizeRunId) {
    if (!sizeRunId) return null
    const sr = this.sizeRunsValue.find(x => String(x.id) === String(sizeRunId))
    return sr?.sizes || null
  }

  getRowSizes(tr) {
    try {
      return JSON.parse(tr.dataset.sizes || "{}")
    } catch {
      return {}
    }
  }

  setRowSizes(tr, alloc) {
    tr.dataset.sizes = JSON.stringify(alloc || {})
  }

  sumAlloc(alloc) {
    return Object.values(alloc || {}).reduce((a, b) => a + (parseInt(b, 10) || 0), 0)
  }

  toInt(v) {
    const n = parseInt(String(v || "").trim(), 10)
    return Number.isFinite(n) ? n : null
  }

  eventRowFromActiveElement() {
    const el = document.activeElement
    if (!el) return null
    return el.closest("tr")
  }

  fetchProductSuggestions(q) {
    const supplierId = this.supplierSelectTarget.value
    if (!supplierId) return

    const term = String(q || "").trim()
    if (term.length < 2) return

    if (this.suggestionAbort) this.suggestionAbort.abort()
    this.suggestionAbort = new AbortController()

    const url = new URL(this.productSuggestionsUrlValue, window.location.origin)
    url.searchParams.set("q", term)
    url.searchParams.set("supplier_id", supplierId)

    fetch(url.toString(), { headers: { "Accept": "application/json" }, signal: this.suggestionAbort.signal })
      .then(r => r.json())
      .then(data => {
        const results = Array.isArray(data.results) ? data.results : []
        // Show unique_code in datalist, but store code as value
        this.productDatalistTarget.innerHTML = results.map(r => 
          `<option value="${this.escapeAttr(r.unique_code || r.code)}" data-code="${this.escapeAttr(r.code)}" data-size-run="${r.size_run_id || ''}">${this.escape(r.unique_code || r.code)}</option>`
        ).join("")
      })
      .catch(err => {
        if (err.name !== "AbortError") console.error(err)
      })
  }

  escape(s) {
    return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  escapeAttr(s) {
    return String(s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;")
  }

  // Centralized method to update import button state
  updateImportButtonState() {
    const hasSupplier = this.supplierSelectTarget.value !== ""
    const hasValidRows = this.previewValidCount > 0
    const hasNoErrors = this.previewErrorCount === 0
    
    const canImport = hasSupplier && hasValidRows && hasNoErrors && !this.inFlight
    this.importButtonTarget.disabled = !canImport
  }

  showProgress() {
    this.progressContainerTarget.classList.remove("hidden")
    this.updateProgress(0, "Iniciando procesamiento...")
  }

  hideProgress() {
    this.progressContainerTarget.classList.add("hidden")
  }

  updateProgress(percentage, text) {
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressTextTarget.textContent = text
  }

  resetAfterSuccessfulImport() {
    // Clear the table
    this.tableBodyTarget.innerHTML = ""
    
    // Reset counters
    if (this.hasValidCountTarget) this.validCountTarget.textContent = "0"
    if (this.hasErrorCountTarget) this.errorCountTarget.textContent = "0"
    if (this.hasTotalAmountTarget) this.totalAmountTarget.textContent = "$0.00"
    
    this.previewValidCount = 0
    this.previewErrorCount = 0
    
    // Reset task ID
    this.importTaskId = null
    
    // Reset form fields (but keep supplier selected for convenience)
    if (this.hasOrderNumberTarget) this.orderNumberTarget.value = ""
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
    
    // Update buttons state
    this.previewButtonTarget.disabled = true
    this.updateImportButtonState()
    
    // Hide progress
    this.hideProgress()
    
    // Add empty rows for new import
    this.addEmptyRows(5)
  }

  // ===========================================================================
  // Product Modal Functions
  // ===========================================================================

  async showCreateProductModal(tr) {
    this.currentModalRow = tr
    const existingProductId = tr.dataset.createdProductId
    const isEdit = existingProductId && existingProductId !== ""

    // Create dialog element
    const dialog = document.createElement("dialog")
    dialog.className = "catalog-dialog catalog-dialog--large"
    dialog.id = "product-create-modal"
    dialog.innerHTML = this.getProductModalHtml()
    document.body.appendChild(dialog)

    this.productModal = dialog
    this.productModalForm = dialog.querySelector("[data-product-modal-target='form']")

    // Setup event listeners
    dialog.addEventListener("close", () => this.handleModalClose())
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) this.closeProductModal()
    })

    // Load catalogs
    await this.loadProductCatalogs()

    // Pre-fill supplier from purchase
    const supplierId = this.supplierSelectTarget.value
    if (supplierId) {
      const supplier1Select = dialog.querySelector("[data-product-modal-target='supplier1Id']")
      if (supplier1Select) supplier1Select.value = supplierId
    }

    // Pre-fill cost from row (parse decimal to handle formatted values)
    const unitPriceRaw = this.getCellValue(tr, "unit_price")
    const unitPrice = this.parseDecimal(unitPriceRaw)
    if (unitPrice && unitPrice > 0) {
      const cost1Input = dialog.querySelector("[data-product-modal-target='cost1']")
      if (cost1Input) cost1Input.value = unitPrice
    }

    // Pre-fill supplier description from row
    const supplierDesc = this.getCellValue(tr, "supplier_description_raw")
    if (supplierDesc) {
      const descInput = dialog.querySelector("[data-product-modal-target='supplierDescription']")
      if (descInput) descInput.value = supplierDesc
    }

    // If editing, load product data
    if (isEdit) {
      await this.loadProductForEdit(existingProductId)
      dialog.querySelector("[data-product-modal-target='title']").textContent = "Editar Producto"
      dialog.querySelector("[data-product-modal-target='deleteBtn']").classList.remove("hidden")
    }

    dialog.showModal()
    document.body.classList.add("modal-open")
  }

  getProductModalHtml() {
    return `
      <div class="dialog-content">
        <div class="dialog-header">
          <h3 class="dialog-title" data-product-modal-target="title">Crear Producto</h3>
          <div class="dialog-header-actions">
            <button type="button" class="btn btn-secondary btn-sm" data-action="click" onclick="this.closest('dialog').dispatchEvent(new CustomEvent('refresh-catalogs'))" title="Actualizar catálogos">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              <span>Actualizar</span>
            </button>
            <button type="button" class="dialog-close" aria-label="Cerrar" onclick="this.closest('dialog').close()">×</button>
          </div>
        </div>

        <div class="dialog-body">
          <form data-product-modal-target="form" class="product-modal-form">
            <input type="hidden" name="product[id]" data-product-modal-target="productId">
            
            <div class="product-modal-errors hidden" data-product-modal-target="errors"></div>

            <div class="product-modal-unique-code">
              <span class="label">Código único:</span>
              <span class="value" data-product-modal-target="uniqueCodeValue">-</span>
              <span class="validation-status" data-product-modal-target="uniqueCodeStatus"></span>
            </div>

            <div class="product-modal-grid">
              <div class="form-group">
                <label class="form-label">Código <span class="required">*</span></label>
                <input type="text" name="product[code]" class="form-control" required data-product-modal-target="code">
              </div>

              <div class="form-group">
                <label class="form-label">Corrida <span class="required">*</span></label>
                <select name="product[size_run_id]" class="form-control" required data-product-modal-target="sizeRunId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Marca</label>
                <select name="product[brand_id]" class="form-control" data-product-modal-target="brandId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Modelo</label>
                <input type="text" name="product[model_name]" class="form-control" list="product-modal-models" autocomplete="off" data-product-modal-target="modelName">
                <datalist id="product-modal-models" data-product-modal-target="modelsDatalist"></datalist>
              </div>

              <div class="form-group">
                <label class="form-label">Color</label>
                <select name="product[color_id]" class="form-control" data-product-modal-target="colorId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Corte</label>
                <select name="product[cut_id]" class="form-control" data-product-modal-target="cutId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Estilo</label>
                <select name="product[style_id]" class="form-control" data-product-modal-target="styleId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Categoría</label>
                <select name="product[category_id]" class="form-control" data-product-modal-target="categoryId">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group full-width">
                <label class="form-label">Descripción Proveedor</label>
                <input type="text" name="product[supplier_description]" class="form-control" data-product-modal-target="supplierDescription">
              </div>

              <div class="form-group">
                <label class="form-label">Proveedor 1</label>
                <select name="product[supplier_1_id]" class="form-control" data-product-modal-target="supplier1Id">
                  <option value="">Seleccionar...</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Costo 1</label>
                <div class="input-with-prefix">
                  <span class="input-prefix">$</span>
                  <input type="number" name="product[cost_1]" step="0.01" min="0" class="form-control input-prefix-field" data-product-modal-target="cost1">
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Precio de venta</label>
                <div class="input-with-prefix">
                  <span class="input-prefix">$</span>
                  <input type="number" name="product[price]" step="0.01" min="0" class="form-control input-prefix-field" data-product-modal-target="price">
                </div>
              </div>

              <div class="form-group"></div>
            </div>
          </form>
        </div>

        <div class="dialog-footer">
          <button type="button" class="btn btn-danger btn-sm hidden" data-product-modal-target="deleteBtn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            </svg>
            Eliminar
          </button>
          <div class="dialog-footer-spacer"></div>
          <button type="button" class="btn btn-secondary" onclick="this.closest('dialog').close()">Cancelar</button>
          <button type="button" class="btn btn-primary" data-product-modal-target="saveBtn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            Guardar Producto
          </button>
        </div>
      </div>
    `
  }

  async loadProductCatalogs() {
    try {
      const response = await fetch(this.productFormDataUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      this.productCatalogs = data

      // Populate selects
      this.populateSelect("sizeRunId", data.size_runs)
      this.populateSelect("brandId", data.brands)
      this.populateSelect("colorId", data.colors)
      this.populateSelect("cutId", data.cuts)
      this.populateSelect("styleId", data.styles)
      this.populateSelect("categoryId", data.categories)
      this.populateSelect("supplier1Id", data.suppliers)

      // Populate models datalist
      const modelsDatalist = this.productModal.querySelector("[data-product-modal-target='modelsDatalist']")
      if (modelsDatalist && data.models) {
        modelsDatalist.innerHTML = data.models.map(m => `<option value="${this.escapeAttr(m.name)}">`).join("")
      }

      // Setup event listeners for unique code validation
      this.setupModalEventListeners()

    } catch (error) {
      console.error("Error loading catalogs:", error)
      toast.error("Error al cargar catálogos")
    }
  }

  populateSelect(targetName, options) {
    const select = this.productModal.querySelector(`[data-product-modal-target='${targetName}']`)
    if (!select || !options) return

    const currentValue = select.value
    select.innerHTML = '<option value="">Seleccionar...</option>' +
      options.map(o => `<option value="${o.id}">${this.escape(o.name)}</option>`).join("")
    
    if (currentValue) select.value = currentValue
  }

  setupModalEventListeners() {
    const codeInput = this.productModal.querySelector("[data-product-modal-target='code']")
    const sizeRunSelect = this.productModal.querySelector("[data-product-modal-target='sizeRunId']")
    const saveBtn = this.productModal.querySelector("[data-product-modal-target='saveBtn']")
    const deleteBtn = this.productModal.querySelector("[data-product-modal-target='deleteBtn']")

    if (codeInput) {
      codeInput.addEventListener("input", () => this.debounceUniqueCodeValidation())
    }
    if (sizeRunSelect) {
      sizeRunSelect.addEventListener("change", () => this.debounceUniqueCodeValidation())
    }
    if (saveBtn) {
      saveBtn.addEventListener("click", () => this.saveProductFromModal())
    }
    if (deleteBtn) {
      deleteBtn.addEventListener("click", () => this.deleteProductFromModal())
    }

    // Refresh catalogs button
    this.productModal.addEventListener("refresh-catalogs", () => this.loadProductCatalogs())
  }

  debounceUniqueCodeValidation() {
    if (this.uniqueCodeTimeout) clearTimeout(this.uniqueCodeTimeout)
    this.uniqueCodeTimeout = setTimeout(() => this.validateUniqueCode(), 500)
  }

  async validateUniqueCode() {
    const codeInput = this.productModal.querySelector("[data-product-modal-target='code']")
    const sizeRunSelect = this.productModal.querySelector("[data-product-modal-target='sizeRunId']")
    const uniqueCodeValue = this.productModal.querySelector("[data-product-modal-target='uniqueCodeValue']")
    const uniqueCodeStatus = this.productModal.querySelector("[data-product-modal-target='uniqueCodeStatus']")
    const productIdInput = this.productModal.querySelector("[data-product-modal-target='productId']")

    const code = codeInput?.value?.trim()
    const sizeRunId = sizeRunSelect?.value
    const excludeId = productIdInput?.value || ""

    if (!code || !sizeRunId) {
      if (uniqueCodeValue) uniqueCodeValue.textContent = "-"
      if (uniqueCodeStatus) {
        uniqueCodeStatus.textContent = ""
        uniqueCodeStatus.className = "validation-status"
      }
      return
    }

    // Find size run name
    const sizeRun = this.productCatalogs?.size_runs?.find(sr => String(sr.id) === String(sizeRunId))
    const previewCode = sizeRun ? `${code} ${sizeRun.name}`.trim() : code
    if (uniqueCodeValue) uniqueCodeValue.textContent = previewCode

    try {
      const url = new URL(this.validateUniqueCodeUrlValue, window.location.origin)
      url.searchParams.set("code", code)
      url.searchParams.set("size_run_id", sizeRunId)
      if (excludeId) url.searchParams.set("exclude_id", excludeId)

      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (uniqueCodeStatus) {
        if (data.valid) {
          uniqueCodeStatus.textContent = "✓ Disponible"
          uniqueCodeStatus.className = "validation-status text-success"
        } else {
          uniqueCodeStatus.textContent = "✗ Ya existe"
          uniqueCodeStatus.className = "validation-status text-danger"
        }
      }

      this.uniqueCodeValid = data.valid
    } catch (error) {
      console.error("Error validating unique code:", error)
    }
  }

  async loadProductForEdit(productId) {
    try {
      const url = this.getProductUrlTemplateValue.replace("__ID__", productId)
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      // Fill form fields
      const setVal = (target, value) => {
        const el = this.productModal.querySelector(`[data-product-modal-target='${target}']`)
        if (el) el.value = value || ""
      }

      setVal("productId", data.id)
      setVal("code", data.code)
      setVal("sizeRunId", data.size_run_id)
      setVal("brandId", data.brand_id)
      setVal("modelName", data.model_name)
      setVal("colorId", data.color_id)
      setVal("cutId", data.cut_id)
      setVal("styleId", data.style_id)
      setVal("categoryId", data.category_id)
      setVal("supplierDescription", data.supplier_description)
      setVal("supplier1Id", data.supplier_1_id)
      setVal("cost1", data.cost_1)
      setVal("price", data.price)

      // Trigger unique code display
      this.validateUniqueCode()

    } catch (error) {
      console.error("Error loading product:", error)
      toast.error("Error al cargar producto")
    }
  }

  async saveProductFromModal() {
    const form = this.productModalForm
    const formData = new FormData(form)
    const productId = formData.get("product[id]")
    const isEdit = productId && productId !== ""

    // Validate required fields
    const code = formData.get("product[code]")?.trim()
    const sizeRunId = formData.get("product[size_run_id]")

    if (!code) {
      this.showModalError("El código es requerido")
      return
    }
    if (!sizeRunId) {
      this.showModalError("La corrida es requerida")
      return
    }

    // Validate unique code
    await this.validateUniqueCode()
    if (!this.uniqueCodeValid) {
      this.showModalError("El código único ya existe")
      return
    }

    const saveBtn = this.productModal.querySelector("[data-product-modal-target='saveBtn']")
    if (saveBtn) {
      saveBtn.disabled = true
      saveBtn.innerHTML = '<span class="spinner-sm"></span> Guardando...'
    }

    try {
      let url, method
      if (isEdit) {
        url = this.updateProductUrlTemplateValue.replace("__ID__", productId)
        method = "PUT"
      } else {
        url = this.createProductUrlValue
        method = "POST"
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (data.success) {
        // Update row with created/updated product
        const tr = this.currentModalRow
        tr.dataset.createdProductId = data.product.id
        tr.dataset.createdProductCode = data.product.code
        tr.dataset.createdProductUniqueCode = data.product.unique_code
        tr.dataset.createdProductSizeRunId = data.product.size_run_id

        // Update PRODUCTO column
        const productTd = tr.querySelector('td[data-field="product_code"]')
        const productInput = productTd?.querySelector('input')
        if (productInput) productInput.value = data.product.code

        // Update CORRIDA column
        const sizeRunTd = tr.querySelector('td[data-field="size_run_id"]')
        const sizeRunSelect = sizeRunTd?.querySelector('select')
        if (sizeRunSelect) sizeRunSelect.value = data.product.size_run_id

        toast.success(isEdit ? "Producto actualizado" : "Producto creado")
        this.closeProductModal()
        this.refreshRowActions(tr)
      } else {
        this.showModalError(data.errors?.join(", ") || "Error al guardar")
      }
    } catch (error) {
      console.error("Error saving product:", error)
      this.showModalError("Error al guardar producto")
    } finally {
      if (saveBtn) {
        saveBtn.disabled = false
        saveBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          Guardar Producto
        `
      }
    }
  }

  async deleteProductFromModal() {
    const productIdInput = this.productModal.querySelector("[data-product-modal-target='productId']")
    const productId = productIdInput?.value

    if (!productId) return

    if (!confirm("¿Eliminar este producto? Esta acción no se puede deshacer.")) return

    try {
      const url = this.destroyProductUrlTemplateValue.replace("__ID__", productId)
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (data.success) {
        // Clear row product data
        const tr = this.currentModalRow
        delete tr.dataset.createdProductId
        delete tr.dataset.createdProductCode
        delete tr.dataset.createdProductUniqueCode
        delete tr.dataset.createdProductSizeRunId

        // Clear PRODUCTO column
        const productTd = tr.querySelector('td[data-field="product_code"]')
        const productInput = productTd?.querySelector('input')
        if (productInput) productInput.value = ""

        toast.success("Producto eliminado")
        this.closeProductModal()
        this.refreshRowActions(tr)
      } else {
        toast.error(data.errors?.join(", ") || "Error al eliminar")
      }
    } catch (error) {
      console.error("Error deleting product:", error)
      toast.error("Error al eliminar producto")
    }
  }

  showModalError(message) {
    const errorsDiv = this.productModal.querySelector("[data-product-modal-target='errors']")
    if (errorsDiv) {
      errorsDiv.textContent = message
      errorsDiv.classList.remove("hidden")
    }
  }

  closeProductModal() {
    if (this.productModal) {
      this.productModal.close()
    }
  }

  handleModalClose() {
    document.body.classList.remove("modal-open")
    if (this.productModal) {
      this.productModal.remove()
      this.productModal = null
    }
    this.currentModalRow = null
    this.uniqueCodeValid = false
  }
}
